import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import {
  MixtureCandidateDetailDto,
  MixtureCandidateDto,
  MixtureCandidateEditDto
} from 'src/store/mixturecandidates/mixturecandidate';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum MixtureCandidateTypes {
  Fetch = 'mixtureCandidates/Fetch',
  FetchAll = 'mixtureCandidates/FetchAll',
  Add = 'mixtureCandidates/Add',
  Update = 'mixtureCandidates/Update',
  Delete = 'mixtureCandidates/Delete',
  Failure = 'mixtureCandidates/Failure'
}

export type MixtureCandidateAction =
  | {
      type: MixtureCandidateTypes.Fetch;
      mixtureCandidate: Nullable<MixtureCandidateDetailDto>;
    }
  | {
      type: MixtureCandidateTypes.FetchAll;
      mixtureCandidates: MixtureCandidateDto[];
    }
  | { type: MixtureCandidateTypes.Add; mixtureCandidate: MixtureCandidateDto }
  | {
      type: MixtureCandidateTypes.Update;
      mixtureCandidate: MixtureCandidateDto;
    }
  | {
      type: MixtureCandidateTypes.Delete;
      mixtureCandidate: MixtureCandidateDto;
    }
  | { type: MixtureCandidateTypes.Failure; errors: any };

export const actionCreators = {
  fetchMixtureCandidate: (
    mixtureCandidate: MixtureCandidateDetailDto
  ): MixtureCandidateAction => ({
    type: MixtureCandidateTypes.Fetch,
    mixtureCandidate: mixtureCandidate
  }),
  fetchMixtureCandidates: (
    mixtureCandidates: MixtureCandidateDto[]
  ): MixtureCandidateAction => ({
    type: MixtureCandidateTypes.FetchAll,
    mixtureCandidates
  }),
  addMixtureCandidate: (
    mixtureCandidate: MixtureCandidateDto
  ): MixtureCandidateAction => ({
    type: MixtureCandidateTypes.Add,
    mixtureCandidate
  }),
  updateMixtureCandidate: (
    mixtureCandidate: MixtureCandidateDto
  ): MixtureCandidateAction => ({
    type: MixtureCandidateTypes.Update,
    mixtureCandidate
  }),
  deleteMixtureCandidate: (
    mixtureCandidate: MixtureCandidateDto
  ): MixtureCandidateAction => ({
    type: MixtureCandidateTypes.Delete,
    mixtureCandidate
  }),
  mixtureCandidateRequestFailure: (errors: any): MixtureCandidateAction => ({
    type: MixtureCandidateTypes.Failure,
    errors
  })
};

export const fetchMixtureCandidate = (id: number) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await getRequest<MixtureCandidateDetailDto>(
      `/mixturecandidates/${id}`
    );
    return ok
      ? dispatch(actionCreators.fetchMixtureCandidate(data))
      : dispatch(actionCreators.mixtureCandidateRequestFailure(error));
  };
};

export const fetchMixtureCandidates = (parameterSetId?: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(MixtureCandidateTypes.FetchAll));
    const { ok, data, error } =
      parameterSetId === undefined
        ? await getRequest<MixtureCandidateDto[]>('/mixturecandidates')
        : await getRequest<MixtureCandidateDto[]>(
            `/mixturecandidates/parameterset/${parameterSetId}`
          );
    dispatch(requestEnd(MixtureCandidateTypes.FetchAll));

    return ok
      ? dispatch(actionCreators.fetchMixtureCandidates(data))
      : dispatch(actionCreators.mixtureCandidateRequestFailure(error));
  };
};

export const createMixtureCandidate = (
  mixtureCandidate: MixtureCandidateEditDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await postRequest(
      '/mixtureCandidates',
      mixtureCandidate
    );
    return ok
      ? dispatch(alertSuccess(i18n.t('mixtureCandidate.created')))
      : dispatch(actionCreators.mixtureCandidateRequestFailure(error));
  };
};

export const editMixtureCandidate = (
  id: number,
  mixtureCandidate: MixtureCandidateEditDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(
      `/mixturecandidates/${id}`,
      mixtureCandidate
    );
    return ok
      ? dispatch(
          actionCreators.updateMixtureCandidate(
            mixtureCandidate as MixtureCandidateDto
          )
        )
      : dispatch(actionCreators.mixtureCandidateRequestFailure(error));
  };
};

export const deleteMixtureCandidate = (
  id: number,
  mixtureCandidate: MixtureCandidateDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/mixturecandidates/${id}`);
    if (ok) {
      dispatch(alertSuccess(i18n.t('mixtureCandidate.deleted')));
      return dispatch(actionCreators.deleteMixtureCandidate(mixtureCandidate));
    }
    dispatch(alertError(error?.errors?.detail ?? i18n.t('delete.failed')));
    return dispatch(actionCreators.mixtureCandidateRequestFailure(error));
  };
};
